import React, { useEffect, useRef, useState } from 'react';
import { ENTER_NAME } from 'Pages/GMS/ISR/ISRConstants';
import Button from 'Components/DesignSystem/Button/Button';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Typography from 'Components/DesignSystem/Typography/Typography';

type TCreateABTestDialogProps = {
    dialogHeader: string;
    labelName: string;
    value?: string;
    actionLabel?: string;
    onClose: () => void;
    onSubmodelFieldSubmit: (submodelName: string) => void;
    showIndicator?: boolean;
    message?: string;
    id?: string;
    multiCreate?: boolean;
    entityName?: string;
}

/**
 * "Create A/B test" on the design-system `Dialog` (`okf/design-system/e2e.md` § 140).
 *
 * What the hand-rolled overlay never had: `role="dialog"`, an accessible name, a focus
 * trap, initial focus, or Escape-to-close. It was a `tw-fixed tw-inset-0` div with a
 * `<h2>` inside it, so a keyboard user could tab straight out of the dialog into the
 * page behind — which is still interactive, because nothing was `inert`.
 *
 * The name input had a `<label>` with no `htmlFor` beside an input whose `id` was
 * conditional on a prop. It is a DS `TextField` now, properly associated.
 *
 * > **The `className` on that input was a string containing the literal text
 * > `className={\`…\`}`** — a copy-paste of the JSX attribute *into* the value. Every
 * > class in it was inert, so the field had been rendering unstyled since it was
 * > written. Deleted rather than repaired; `TextField` owns the chrome now.
 *
 * **`multiCreate` is preserved and still dead.** No call site passes it, and A/B
 * Testing's own only ever renders the single-create branch. It is kept because the
 * prop is part of the component's published shape, and removing it would be a separate
 * decision from this migration — but note the trap it carries, recorded in
 * `okf/abtesting/e2e.md` § 4: `ab-create-cancel-btn` names Cancel in BOTH branches, so
 * that one testid maps to two code paths.
 */
const CreateABTestDialog = ({
    dialogHeader = "", labelName = "", value,
    onClose, onSubmodelFieldSubmit,
    actionLabel, showIndicator = false,
    message = 'Creating your model ...', id,
    multiCreate = false, entityName = 'Item'
}: TCreateABTestDialogProps) => {
    const [submodelName, setSubmodelName] = useState(value ?? '');
    const [justCreated, setJustCreated] = useState(false);

    /*
      The "✓ Created" flash is a 2s timer. It was never cancelled, so closing the
      dialog inside that window left a `setJustCreated` pending against an unmounted
      tree — the same shape as the `/admin/settings` locks (§ 134). Only reachable
      through `multiCreate`, which nothing passes today; fixed anyway, because the
      alternative is leaving a known leak behind a flag someone will eventually set.
    */
    const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => () => {
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    }, []);

    const baseId = id || 'ab-test-create-dialog';

    const handleCreateAndAddMore = () => {
        onSubmodelFieldSubmit(submodelName);
        setSubmodelName("");
        setJustCreated(true);
        if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
        flashTimerRef.current = setTimeout(() => setJustCreated(false), 2000);
    };

    const handleCreate = () => {
        onSubmodelFieldSubmit(submodelName);
        onClose();
    };

    const handleSingleCreate = () => {
        onSubmodelFieldSubmit(submodelName);
        setSubmodelName("");
    };

    /*
        § 209 — while the parent's create is in flight (`showIndicator`;
        ABHomePage passes it around `createAbTest`, resetting in `finally`) the
        dialog is COMMITTED: the call is not abortable, and a mid-flight close
        would only hide the dialog while the toast + navigation still land. One
        guard covers every close route (Cancel, the X, Escape — Dialog routes
        all three through `onClose`).
    */
    const guardedClose = () => {
        if (showIndicator) return;
        onClose();
    };

    const footer = (
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-w-full">
            {/*
              The "Creating AB test ..." indicator. It was a bare `<label>`; as a live
              region it is now actually announced, which is the whole reason a caller
              renders progress text at all.
            */}
            <div className="tw-min-h-5">
                {showIndicator ? (
                    <Typography
                        variant="sub-body-supporting"
                        as="span"
                        role="status"
                        className="tw-text-ink-tertiary"
                        data-testid={`${baseId}-indicator`}
                    >
                        {message}
                    </Typography>
                ) : null}
            </div>
            <div className="tw-flex tw-gap-2 tw-justify-end">
                {multiCreate ? (
                    <>
                        <Button
                            variant="secondary"
                            id={id ? `${id}-cancel-btn` : undefined}
                            data-testid="ab-create-cancel-btn"
                            disabled={showIndicator}
                            onClick={guardedClose}
                        >
                            Cancel
                        </Button>
                        {justCreated ? (
                            <Button
                                variant="primary"
                                id={id ? `${id}-add-more-btn` : undefined}
                                data-testid="components-createabtestdialog-button-2"
                                disabled
                            >
                                ✓ {entityName} Created
                            </Button>
                        ) : (
                            <Button
                                variant="secondary"
                                id={id ? `${id}-add-more-btn` : undefined}
                                data-testid="components-createabtestdialog-button-3"
                                disabled={submodelName === ""}
                                loading={showIndicator}
                                onClick={handleCreateAndAddMore}
                            >
                                Create &amp; Add More
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            id={id ? `${id}-submit-btn` : undefined}
                            data-testid="components-createabtestdialog-button-4"
                            disabled={submodelName === ""}
                            loading={showIndicator}
                            onClick={handleCreate}
                        >
                            Create
                        </Button>
                    </>
                ) : (
                    <>
                        <Button
                            variant="secondary"
                            id={id ? `${id}-cancel-btn` : undefined}
                            data-testid="ab-create-cancel-btn"
                            disabled={showIndicator}
                            onClick={guardedClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            id={id ? `${id}-submit-btn` : undefined}
                            data-testid="components-createabtestdialog-button-5"
                            disabled={submodelName === ""}
                            loading={showIndicator}
                            onClick={handleSingleCreate}
                        >
                            {actionLabel ?? 'Create'}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );

    return (
        /*
          `isOpen` is hardcoded true because every call site already gates the MOUNT
          (`{showDialog ? <CreateABTestDialog … /> : null}`) — the same arrangement
          § 128's Category Import drawer landed on. Threading an `isOpen` prop through
          would give two sources of truth for one piece of state.

          `Dialog` takes no `id`, so the close button's hook is templated from the root
          testid: `ab-create-close-btn` is retired for `<baseId>-close`. Cancel and
          Submit keep BOTH their `id`s and their original testids, which several specs
          key off.
        */
        <Dialog
            isOpen
            onClose={guardedClose}
            title={dialogHeader}
            size="md"
            data-testid={baseId}
            footer={footer}
        >
            <TextField
                label={labelName}
                id={id ? `${id}-name-input` : "create_abtest"}
                data-testid="components-createabtestdialog-input-1"
                value={submodelName}
                onChange={setSubmodelName}
                placeholder={ENTER_NAME}
            />
        </Dialog>
    );
}

export default CreateABTestDialog
